/**
 * MirrorKpiModal — M3 Dialog de origem do dado.
 *
 * Exibido ao clicar em qualquer card do bento.
 * Mostra: fonte, conta, periodo, nota explicativa e CTA para o chat.
 *
 * A11y: role="dialog", aria-modal, aria-labelledby, foco gerenciado pelo
 * componente pai (MirrorPage fecha ao pressionar Escape e ao clicar no overlay).
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export interface MirrorKpiModalData {
  readonly title: string;
  readonly value: string;
  readonly sourceName: string;
  readonly account: string;
  readonly period: string;
  readonly collectedAt: string;
  readonly note: string;
}

interface MirrorKpiModalProps {
  readonly data: MirrorKpiModalData;
  readonly onClose: () => void;
}

export function MirrorKpiModal({ data, onClose }: MirrorKpiModalProps): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);

  /* Foca o botao de fechar ao abrir */
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  /* Fecha ao pressionar Escape */
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="mirror-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="mirror-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mirror-modal-title"
      >
        <div className="mirror-modal-header">
          <div className="mirror-modal-label">Origem do dado</div>
          <div className="mirror-modal-title" id="mirror-modal-title">
            {data.title}
          </div>
          <div className="mirror-modal-value" aria-label={`Valor: ${data.value}`}>
            {data.value}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="mirror-modal-close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <span className="mirror-icon" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="mirror-modal-body">
          <dl className="mirror-modal-dl">
            <dt className="mirror-modal-dt">Fonte</dt>
            <dd className="mirror-modal-dd">{data.sourceName}</dd>
            <dt className="mirror-modal-dt">Conta</dt>
            <dd className="mirror-modal-dd">{data.account}</dd>
            <dt className="mirror-modal-dt">Periodo</dt>
            <dd className="mirror-modal-dd">{data.period}</dd>
            <dt className="mirror-modal-dt">Coletado</dt>
            <dd className="mirror-modal-dd">{data.collectedAt}</dd>
          </dl>

          <p className="mirror-modal-note">{data.note}</p>

          <Link
            to="/chat"
            className="mirror-modal-cta"
            style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
          >
            Perguntar a CROSS sobre isso
          </Link>
        </div>
      </div>
    </div>
  );
}
